import { FormEvent, useState } from "react"
import { useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { TillStockCount } from "@/features/staff/till-stock-count"
import {
  initialStockDrafts,
  pendingStockDrafts,
  stockCountPayload,
  stockDraftsReady,
  TILL_PATHS,
  tTill,
  type TillItemStock,
} from "@/features/staff/till"

/**
 * What the till screen shows when no shift is open.
 *
 * The empty state *is* the form. A card that explains the situation and then
 * asks for a tap to reveal the one field it needs is two interactions where
 * there is one decision, and the cashier standing at the counter at eight in
 * the morning made that decision before the screen loaded. Nothing here is
 * revealed progressively.
 *
 * The copy also has a job. It says out loud that the diner's QR rail keeps
 * working: a cashier who reads "the till is closed" and concludes the
 * restaurant cannot take money is a cashier who turns away a sale that would
 * have settled fine. Only the rails *staff* originate — cash and the card
 * terminal — need a shift open.
 *
 * ## The arqueo is part of the same decision
 *
 * Where the restaurant counts dishes, opening the day means two things —
 * "arranco con este fondo" and "arranco con estos platillos" — and they commit
 * together, in one request, which the server writes in one transaction. There
 * is no second screen and no second submit: a half-opened day, with a drawer
 * open and yesterday's counts still on the floor, is the state this refuses to
 * make reachable.
 *
 * `transform` rather than holding the counts in the form's own data: the drafts
 * carry per-row UI state (confirmed vs. merely prefilled) that the server has
 * no business receiving, so the payload is built at submit from the drafts.
 */

type TillOpenFormProps = {
  hadPreviousShift: boolean
  /** Null at a restaurant that does not count dishes: no arqueo, no section. */
  itemStock: TillItemStock | null
}

export function TillOpenForm({ hadPreviousShift, itemStock }: TillOpenFormProps) {
  const stocks = itemStock?.stocks ?? []
  const stockKey = stocks.map((stock) => stock.id).join(",")

  const [drafts, setDrafts] = useState(() => initialStockDrafts(stocks))
  // Re-seed when the set of counted dishes changes underneath the form, using
  // React's adjust-state-during-render pattern rather than an effect (no extra
  // pass, nothing to clean up). Without it the drafts and the rows drift apart:
  // a dish added since the form mounted has no draft, and a form that renders
  // its rows from either list alone would silently drop it while still
  // reporting itself complete. Resetting the confirmations is the right answer
  // when the list itself moved -- what was signed for was a different list.
  const [seededFor, setSeededFor] = useState(stockKey)
  if (seededFor !== stockKey) {
    setSeededFor(stockKey)
    setDrafts(initialStockDrafts(stocks))
  }

  const { data, setData, post, processing, transform } = useForm({ opening_float: "" })

  const counting = stocks.length > 0
  const pending = pendingStockDrafts(drafts)
  const ready = !counting || stockDraftsReady(drafts)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!ready) return

    transform((current) => (counting ? { ...current, stock_counts: stockCountPayload(drafts) } : current))
    post(TILL_PATHS.open, { preserveScroll: true })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{tTill(hadPreviousShift ? "closed_title" : "never_opened_title")}</CardTitle>
        <CardDescription>{tTill("closed_body")}</CardDescription>
      </CardHeader>

      <form onSubmit={handleSubmit} data-testid="shift-open-form">
        <CardContent className="space-y-6">
          <div className="max-w-64 space-y-2">
            <Label htmlFor="opening-float">{tTill("opening_float_label")}</Label>
            <Input
              id="opening-float"
              data-testid="shift-opening-float"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              required
              autoComplete="off"
              className="tabular-nums"
              aria-describedby="opening-float-hint"
              value={data.opening_float}
              onChange={(event) => setData("opening_float", event.target.value)}
            />
            <p id="opening-float-hint" className="text-xs text-muted-foreground">
              {tTill("opening_float_hint")}
            </p>
          </div>

          {itemStock ? (
            <div className="border-t pt-6">
              <TillStockCount stocks={stocks} drafts={drafts} onChange={setDrafts} />
            </div>
          ) : null}
        </CardContent>

        <CardFooter className="mt-6 flex-wrap gap-3">
          <Button
            type="submit"
            disabled={processing || !ready}
            data-testid="shift-open-submit"
          >
            {tTill(counting ? "open_submit_with_stock" : "open_submit")}
          </Button>

          {pending > 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="shift-open-pending">
              {tTill("stock_pending", { count: pending })}
            </p>
          ) : null}
        </CardFooter>
      </form>
    </Card>
  )
}
